import { Component, DestroyRef, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PlanogramActivityModalComponent } from '../../modals/planogram-activity-modal/planogram-activity-modal.component';
import { PlanogramSummaryDTO, RecentEventResults } from '../../models/sharing-center';
import { PlanogramStatusType } from '../../models/planogram-status';
import { SharingCenterService } from '../../services/sharingcenter.service';
import { FolderApiService } from '../../services/folder-api.service';
import { ModalService } from '../../services/modal.service';
import { PlanogramApiService } from '../../services/planogram-api.service';
import { EventDTO } from 'src/app/models/events';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { Router } from '@angular/router';
import { PlanogramService } from 'src/app/services/planogram.service';
import { FormsModule } from '@angular/forms';
import { NgIf, NgClass, NgFor, DatePipe } from '@angular/common';

@Component({
    selector: 'app-activity-center-page',
    templateUrl: './sharing-center.component.html',
    styleUrl: './sharing-center.component.scss',
    encapsulation: ViewEncapsulation.None,
    imports: [FormsModule, NgIf, NgClass, NgFor, DatePipe]
})
export class SharingCenterComponent implements OnInit {
  planogramSummaries: PlanogramSummaryDTO[] = [];
  recentEvents: EventDTO[] = [];

  currentPlanogramPage = 1;
  currentEventsPage = 1;
  totalPlanogramPages = 1;
  totalEventsPages = 1;

  pageSizeSummaries = 50;
  pageSizeEvents = 100;

  loadingSummaries = false;
  loadingEvents = false;

  totalActivePlanograms = 0;
  totalPendingFeedback = 0;
  distinctViewers = 0;
  
  // Search & Sort props
  searchTerm: string = '';
  sortBy: string = 'activity';
  sortAsc: boolean = false;
  private searchSubject = new Subject<string>();

  private destroyRef = inject(DestroyRef);

  constructor(
    private router: Router,
    private sharingCenterService: SharingCenterService,
    private folderApi: FolderApiService,
    private planogramService: PlanogramService,
    private planogramApi: PlanogramApiService,
    private modalService: ModalService
  ) {
    
    // Setup search debouncer
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(term => {
      this.searchTerm = term;
      this.currentPlanogramPage = 1; // Reset to page 1 on new search
      this.loadPlanogramSummaries();
    });
  }

  ngOnInit(): void {
    this.loadPlanogramSummaries();
    this.loadRecentEvents();
  }

  onSearch(term: string): void {
    this.searchSubject.next(term);
  }

  getSortIcon(column: string): string {
    if (this.sortBy !== column) return 'bi-arrow-down-up text-muted opacity-25';
    return this.sortAsc ? 'bi-arrow-up text-dark' : 'bi-arrow-down text-dark';
  }

  toggleSort(column: string): void {
    if (this.sortBy === column) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortBy = column;
      this.sortAsc = true;
    }
    this.currentPlanogramPage = 1; // Reset to page 1 on sort change
    this.loadPlanogramSummaries();
  }

  loadPlanogramSummaries(): void {
    this.loadingSummaries = true;
    this.sharingCenterService
      .getPlanogramSummaries(
        this.currentPlanogramPage,
        this.pageSizeSummaries,
        this.searchTerm,
        this.sortBy,
        this.sortAsc
      )
      .subscribe((res) => {
        this.loadingSummaries = false;
        if (res) {
          this.planogramSummaries = res.summaries;
          this.totalPlanogramPages = res.totalPages;
          
          // {{ Update Stats from Backend Source }}
          if (res.globalStats) {
            this.totalActivePlanograms = res.globalStats.totalActive;
            this.totalPendingFeedback = res.globalStats.pendingFeedback;
            this.distinctViewers = res.globalStats.uniqueViewers;
          }
        }
      });
  }

  loadRecentEvents(): void {
    this.loadingEvents = true;
    this.sharingCenterService
      .getRecentEvents(this.currentEventsPage, this.pageSizeEvents)
      .subscribe((res: RecentEventResults | null) => {
        this.loadingEvents = false;
        if (res) {
          this.recentEvents = res.events;
          this.totalEventsPages = res.totalPages;
        }
      });
  }

  changePlanogramPage(page: number): void {
    if (page >= 1 && page <= this.totalPlanogramPages) {
      this.currentPlanogramPage = page;
      this.loadPlanogramSummaries();
    }
  }

  changeEventsPage(page: number): void {
    if (page >= 1 && page <= this.totalEventsPages) {
      this.currentEventsPage = page;
      this.loadRecentEvents();
    }
  }

  async viewPlanogram(planogramId: string) {
    try {
      const planogram = await this.folderApi.getSharedPlanogram(planogramId).toPromise();
      const state = planogram?.states[0];
      if (planogram && state) {
        this.router.navigate([`/planogram/${planogram.guid}/${state.guid}`]);
      }
    } catch (err) {
      console.error('Failed to open shared planogram', err);
    }
  }

  openPlanogramActivity(planogramId: string): void {
    const planogram = this.planogramSummaries.find(p => p.planogramId === planogramId);
    this.modalService.openModalWithOptions(
      PlanogramActivityModalComponent,
      { size: 'xl', backdrop: 'static', centered: true },
      {
        planogramId,
        planogramName: planogram ? `${planogram.storeName} - ${planogram.merchName}` : 'Planogram'
      }
    );
  }
}